/**
 * "Reading this map" page-level legend affordance for Engine Atlas.
 * Per scope doc §3 + §8 "Reading this map page-level affordance" follow-up
 * entry. Carries layers 1-3 from the 11-layer progression — the geography-
 * encoded layers that don't surface as per-region inspect content.
 *
 * Collapsed by default; admin clicks "Reading this map" affordance to
 * reveal a brief explainer covering geography (axes + middle-cell engine
 * concentration), reading direction (how cells contribute to position
 * synthesis surfaced in the title block above), and layer reference (the
 * 7 inspect layers that surface per region V1, + the 8th composability
 * layer reserved for post-Day-92 wire-up).
 *
 * Style: borderless prose-only per P4.5 Sol lock — card framing competes
 * with title-block centerpiece weight; legend-altitude content stays
 * subdued. Collapsed affordance reads text-ink-muted; hover bumps to
 * text-ink-secondary. Section labels are sentence-case (not uppercase)
 * for position-surface character over UI-convention.
 *
 * Layer naming verbatim per scope doc §6 (Phase 0 item 13 PASS): math vs
 * judgment / mechanical vs strategic / operational vs ownership / trace /
 * propagation / reversibility / positional contribution / composability
 * (8th, reserved).
 *
 * No substrate dependency — renders identically at empty engine state.
 * Sticky-panel + scroll-spy navigation deferred to Atlas Day 2-3 per
 * scope doc §2.
 */

import { useState, type JSX } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

export default function ReadingThisMap(): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="reading-this-map-body"
        className="flex items-center gap-2 py-1 text-xs text-ink-muted hover:text-ink-secondary transition-colors cursor-pointer"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Reading this map</span>
      </button>

      {expanded && (
        <div
          id="reading-this-map-body"
          className="mt-3 space-y-3 text-sm text-ink-secondary"
        >
          <section>
            <h3 className="text-xs text-ink-muted font-semibold mb-1">Geography</h3>
            <p>
              Engine Atlas is organized as a 3×3 map. The vertical axis runs
              from substrate (bottom) to position (top); the horizontal axis
              runs from input (left) through calculation (middle) to decision
              (right). Calculations and judgments emerge at the middle × middle
              cell — not placed there, but where they fall when altitude and
              stage are honored as axes.
            </p>
          </section>

          <section>
            <h3 className="text-xs text-ink-muted font-semibold mb-1">Reading direction</h3>
            <p>
              Each occupied region shows configuration that contributes to
              ISG&apos;s position, synthesized in the title block above. Click
              any region to inspect what it carries.
            </p>
          </section>

          <section>
            <h3 className="text-xs text-ink-muted font-semibold mb-1">Layers</h3>
            <p>
              Inspect surfaces seven layers per region: math vs judgment,
              mechanical vs strategic, operational vs ownership, trace,
              propagation, reversibility, and positional contribution. An
              eighth layer — composability — wires when audit trail substrate
              ships.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
